"use client";

import { useState } from "react";
import {
  Calendar,
  Clock,
  Brain,
  Database,
  Globe,
  GraduationCap,
  Server,
  User,
  Cpu,
  BarChart,
  BotIcon as Robot,
  LineChart,
  Microscope,
} from "lucide-react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Footer from "../Header/Footer";
import Navbar from "../Header/Navbar";
import ApplyButton from "../common/ApplyButton";

const MsaiPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [showAllSemesters, setShowAllSemesters] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className="font-sans">
      {/* Header */}
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-purple-800 to-purple-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                Master of Science in Artificial Intelligence
              </h1>
              <p className="text-xl mb-6 text-purple-100">
                Develop cutting-edge AI expertise and prepare for leadership
                roles in the rapidly evolving field of artificial intelligence
              </p>
              {/* Apply Button */}
              <div className="flex flex-col md:flex-row gap-4 mt-8">
                <ApplyButton
                  to="/login"
                  className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300 shadow-lg transform hover:scale-105"
                >
                  Apply Now
                </ApplyButton>
              </div>
            </div>
            <div
              className="md:w-1/2"
              style={{ height: "30rem", width: "43rem" }}
            >
              <img
                src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?q=80&w=2069&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                alt="MSAI Students"
                className="rounded-lg shadow-2xl transform hover:scale-105 transition duration-300"
                style={{ objectFit: "cover", height: "100%", width: "100%" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Info */}
      <section className="bg-gray-100 py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg transform hover:scale-105 transition duration-300">
              <Calendar className="text-purple-600 mb-4" size={48} />
              <h3 className="font-semibold text-gray-800 text-xl mb-2">
                Program Duration
              </h3>
              <p className="text-gray-600">2 Years (4 Semesters)</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg transform hover:scale-105 transition duration-300">
              <GraduationCap className="text-purple-600 mb-4" size={48} />
              <h3 className="font-semibold text-gray-800 text-xl mb-2">
                Degree Awarded
              </h3>
              <p className="text-gray-600">MS in Artificial Intelligence</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg transform hover:scale-105 transition duration-300">
              <Clock className="text-purple-600 mb-4" size={48} />
              <h3 className="font-semibold text-gray-800 text-xl mb-2">
                Credit Hours
              </h3>
              <p className="text-gray-600">30+ Credit Hours</p>
            </div>
          </div>
        </div>
      </section>

      {/* Program Tabs */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-center text-gray-800 mb-4">
              Program Details
            </h2>
            <p className="text-center text-gray-600 max-w-5xl mx-auto text-lg">
              The Master of Science in Artificial Intelligence (MSAI) at NIIT,
              Lahore, is a 2-year HEC-recognized program comprising 31 credit
              hours over 4 semesters. It is designed to provide in-depth
              expertise in AI theory and applications, combining foundational
              knowledge with advanced techniques in machine learning, deep
              learning, and intelligent systems. The program offers
              specialization in various streams including Big Data Analytics,
              Deep Learning, Computer Vision, Data Mining, Intelligent
              Computing, Machine Learning, and Artificial Intelligence
              Engineering. Students can pursue thesis or non-thesis tracks,
              supported by expert supervision and access to modern AI labs.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center border-b border-gray-200 mb-8">
            {["overview", "curriculum", "admission", "careers"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 px-6 font-medium text-sm focus:outline-none transition duration-300 ${
                  activeTab === tab
                    ? "border-b-2 border-purple-600 text-purple-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white p-8 rounded-lg shadow-lg">
            {activeTab === "overview" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Program Overview
                </h3>
                <p className="text-gray-700 mb-6">
                  The MSAI program offers rigorous training in designing and
                  deploying intelligent solutions across diverse domains.
                  Students explore core areas such as natural language
                  processing, computer vision, robotics, and data science. The
                  curriculum is developed to equip students with both
                  theoretical acumen and applied competencies necessary to drive
                  innovation in the AI sector.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {[
                    {
                      icon: <Brain className="text-purple-600" size={36} />,
                      title: "Focused AI Specializations",
                      description:
                        "Master cutting-edge topics including machine learning, neural networks, computer vision, and reinforcement learning. Elective options allow students to align their studies with specific career or research aspirations.",
                    },
                    {
                      icon: <User className="text-purple-600" size={36} />,
                      title: "AI Research Mentorship",
                      description:
                        "Work closely with experienced faculty on AI research projects addressing real-world challenges. Students gain opportunities to publish, present, and collaborate on funded research initiatives.",
                    },
                    {
                      icon: (
                        <Microscope className="text-purple-600" size={36} />
                      ),
                      title: "Tech-Driven Industry Exposure",
                      description:
                        "Benefit from strong collaboration with AI-focused companies and innovation hubs. Internship programs, guest lectures, and capstone projects provide valuable industry engagement and career readiness.",
                    },
                    {
                      icon: <Globe className="text-purple-600" size={36} />,
                      title: "Academic Leadership",
                      description:
                        "Learn from leading AI researchers and practitioners who bring a blend of theoretical insight and applied expertise. Their mentorship fosters academic excellence and professional growth.",
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start bg-gray-50 p-6 rounded-lg transform hover:scale-105 transition duration-300"
                    >
                      <div className="bg-white p-3 rounded-full mr-4 shadow-md">
                        {item.icon}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 text-lg mb-2">
                          {item.title}
                        </h4>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "curriculum" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Curriculum
                </h3>
                <p className="text-gray-700 mb-6">
                  Our comprehensive curriculum is designed to provide advanced
                  knowledge in artificial intelligence while offering
                  specialization opportunities in emerging AI technologies.
                </p>

                <div className="mb-8">
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Streams Available
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {[
                      "Big Data Analytics",
                      "Deep Learning",
                      "Computer Vision",
                      "Data Mining",
                      "Intelligent Computing",
                      "Machine Learning",
                      "Artificial Intelligence Engineering",
                    ].map((stream, index) => (
                      <div
                        key={index}
                        className="bg-purple-50 p-3 rounded-lg border border-purple-200 text-center"
                      >
                        <p className="font-medium text-gray-800">{stream}</p>
                      </div>
                    ))}
                  </div>

                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Scheme of Studies
                  </h4>

                  {/* Semester 1 */}
                  <div className="mb-8">
                    <h5 className="text-lg font-semibold text-purple-700 mb-3">
                      Semester I
                    </h5>
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-b">
                              Course
                            </th>
                            <th className="py-3 px-4 text-center text-sm font-medium text-gray-700 border-b">
                              Credits
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="hover:bg-gray-50">
                            <td className="py-2 px-4 border-b text-sm">
                              Mathematics for Artificial Intelligence (Core-I)
                            </td>
                            <td className="py-2 px-4 border-b text-sm text-center">
                              3
                            </td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="py-2 px-4 border-b text-sm">
                              Machine Learning (Core-II)
                            </td>
                            <td className="py-2 px-4 border-b text-sm text-center">
                              3
                            </td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="py-2 px-4 border-b text-sm">
                              Research Methodology (Core-III)
                            </td>
                            <td className="py-2 px-4 border-b text-sm text-center">
                              1
                            </td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="py-2 px-4 border-b text-sm">
                              Advanced Artificial Intelligence (Core-IV)
                            </td>
                            <td className="py-2 px-4 border-b text-sm text-center">
                              3
                            </td>
                          </tr>
                          <tr className="bg-purple-50 font-medium">
                            <td className="py-2 px-4 border-b text-sm">
                              Semester Total
                            </td>
                            <td className="py-2 px-4 border-b text-sm text-center">
                              10
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Semester 2 */}
                  <div className="mb-8">
                    <h5 className="text-lg font-semibold text-purple-700 mb-3">
                      Semester II
                    </h5>
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-b">
                              Course
                            </th>
                            <th className="py-3 px-4 text-center text-sm font-medium text-gray-700 border-b">
                              Credits
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="hover:bg-gray-50">
                            <td className="py-2 px-4 border-b text-sm">
                              Advanced Analysis of Algorithms (Core-V)
                            </td>
                            <td className="py-2 px-4 border-b text-sm text-center">
                              3
                            </td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="py-2 px-4 border-b text-sm">
                              Elective-I
                            </td>
                            <td className="py-2 px-4 border-b text-sm text-center">
                              3
                            </td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="py-2 px-4 border-b text-sm">
                              Elective-II
                            </td>
                            <td className="py-2 px-4 border-b text-sm text-center">
                              3
                            </td>
                          </tr>
                          <tr className="bg-purple-50 font-medium">
                            <td className="py-2 px-4 border-b text-sm">
                              Semester Total
                            </td>
                            <td className="py-2 px-4 border-b text-sm text-center">
                              9
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Show More Button */}
                  {!showAllSemesters && (
                    <div className="flex justify-center mt-8 mb-8">
                      <button
                        onClick={() => setShowAllSemesters(true)}
                        className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-6 rounded-lg transition duration-300 shadow-md"
                      >
                        Show More Semesters
                      </button>
                    </div>
                  )}

                  {/* Semesters 3-4 (conditionally rendered) */}
                  {showAllSemesters && (
                    <>
                      {/* Semester 3 */}
                      <div className="mb-8">
                        <h5 className="text-lg font-semibold text-purple-700 mb-3">
                          Semester III
                        </h5>
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-b">
                                  Course
                                </th>
                                <th className="py-3 px-4 text-center text-sm font-medium text-gray-700 border-b">
                                  Credits
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="hover:bg-gray-50">
                                <td className="py-2 px-4 border-b text-sm">
                                  Elective-III
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  3
                                </td>
                              </tr>
                              <tr className="hover:bg-gray-50">
                                <td className="py-2 px-4 border-b text-sm">
                                  Elective-IV
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  3
                                </td>
                              </tr>
                              <tr className="hover:bg-gray-50">
                                <td className="py-2 px-4 border-b text-sm">
                                  MS Thesis
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  6
                                </td>
                              </tr>
                              <tr className="bg-purple-50 font-medium">
                                <td className="py-2 px-4 border-b text-sm">
                                  Semester Total
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  12
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Semester 4 */}
                      <div className="mb-8">
                        <h5 className="text-lg font-semibold text-purple-700 mb-3">
                          Semester IV
                        </h5>
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-b">
                                  Course
                                </th>
                                <th className="py-3 px-4 text-center text-sm font-medium text-gray-700 border-b">
                                  Credits
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="hover:bg-gray-50">
                                <td className="py-2 px-4 border-b text-sm">
                                  MS Thesis (continue)
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  -
                                </td>
                              </tr>
                              <tr className="bg-purple-50 font-medium">
                                <td className="py-2 px-4 border-b text-sm">
                                  Total Degree Credit Hours
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  31
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-8 bg-purple-50 p-6 rounded-lg shadow-md">
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Program Summary
                  </h4>
                  <p className="text-gray-700 mb-4">
                    The MS Artificial Intelligence program requires a total of
                    31 credit hours to graduate, with a mix of core courses,
                    electives, and a thesis component.
                  </p>
                </div>
              </div>
            )}

            {activeTab === "admission" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Admission Requirements
                </h3>
                <p className="text-gray-700 mb-6">
                  To be eligible for admission to the MSAI program, applicants
                  must meet the following requirements:
                </p>

                <div className="space-y-8">
                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Academic Requirements
                    </h4>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700">
                      <li>
                        Four-year HEC-recognized BS degree/equivalent degree in
                        the relevant field with at least 120 CH beyond 12 years
                        of schooling
                      </li>
                      <li>
                        CGPA of at least 2.00 (on a scale of 4.00) OR First
                        Division under the annual system
                      </li>
                      <li>
                        In the case of a foreign degree, HEC equivalence is
                        mandatory
                      </li>
                      <li>
                        Strong background in mathematics, statistics, and
                        programming
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Entrance Test
                    </h4>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700">
                      <li>
                        Qualifying score (50% or above) in the University MS
                        Admission Test
                      </li>
                      <li>
                        OR evidence of having passed the GRE/Equivalent test
                        with a minimum of 50% marks
                      </li>
                      <li>
                        Test covers Mathematics, Statistics, Programming, and AI
                        fundamentals
                      </li>
                    </ul>
                  </div>

                  {/* <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Required Documents
                    </h4>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700">
                      <li>Completed application form</li>
                      <li>
                        Official transcripts from all post-secondary
                        institutions attended
                      </li>
                      <li>Statement of purpose</li>
                      <li>Two letters of recommendation</li>
                      <li>Updated curriculum vitae/resume</li>
                      <li>Copy of CNIC</li>
                      <li>Four recent passport-sized photographs</li>
                    </ul>
                  </div> */}

                  <div className="bg-purple-50 p-6 rounded-lg shadow-md border-l-4 border-purple-500">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Application Process
                    </h4>
                    <ol className="list-decimal pl-5 space-y-2 text-gray-700">
                      <li>Submit online application through the NIIT portal</li>
                      <li>Pay application fee</li>
                      <li>Appear for entrance test on the scheduled date</li>
                      <li>
                        Shortlisted candidates will be called for interview
                      </li>
                      <li>
                        Final selection based on academic record, entrance test,
                        and interview performance
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "careers" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Career Prospects
                </h3>
                <p className="text-gray-700 mb-6">
                  Graduates of our MSAI program are well-prepared for advanced
                  career opportunities in the rapidly growing AI sector. The
                  program equips students with the expertise needed to excel in
                  various roles.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Job Opportunities
                    </h4>
                    <ul className="space-y-4">
                      {[
                        {
                          icon: <Brain size={20} />,
                          title: "AI Engineer",
                          description:
                            "Design, develop and deploy AI models and systems",
                        },
                        {
                          icon: <Database size={20} />,
                          title: "Machine Learning Engineer",
                          description:
                            "Build and optimize machine learning models for production",
                        },
                        {
                          icon: <BarChart size={20} />,
                          title: "Data Scientist",
                          description:
                            "Extract insights from data using AI and statistical methods",
                        },
                        {
                          icon: <Robot size={20} />,
                          title: "AI Research Scientist",
                          description:
                            "Advance the state-of-the-art in artificial intelligence",
                        },
                      ].map((job, index) => (
                        <li key={index} className="flex items-start">
                          <div className="bg-purple-100 p-2 rounded-full mr-3 mt-1">
                            {job.icon}
                          </div>
                          <div>
                            <span className="font-medium text-gray-800">
                              {job.title}
                            </span>
                            <p className="text-sm text-gray-600">
                              {job.description}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Advanced Opportunities
                    </h4>
                    <ul className="space-y-4">
                      {[
                        {
                          icon: <LineChart size={20} />,
                          title: "AI Product Manager",
                          description:
                            "Lead the development of AI-powered products and features",
                        },
                        {
                          icon: <Cpu size={20} />,
                          title: "AI Ethics Officer",
                          description:
                            "Ensure ethical implementation of AI systems",
                        },
                        {
                          icon: <Globe size={20} />,
                          title: "AI Consultant",
                          description:
                            "Advise organizations on AI strategy and implementation",
                        },
                        {
                          icon: <Server size={20} />,
                          title: "AI Startup Founder",
                          description:
                            "Create innovative AI solutions as an entrepreneur",
                        },
                      ].map((job, index) => (
                        <li key={index} className="flex items-start">
                          <div className="bg-purple-100 p-2 rounded-full mr-3 mt-1">
                            {job.icon}
                          </div>
                          <div>
                            <span className="font-medium text-gray-800">
                              {job.title}
                            </span>
                            <p className="text-sm text-gray-600">
                              {job.description}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="bg-purple-50 p-6 rounded-lg shadow-md mb-8">
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Industry Sectors
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      "Technology Companies",
                      "Healthcare",
                      "Financial Services",
                      "Automotive",
                      "E-commerce",
                      "Manufacturing",
                      "Education",
                      "Research Institutions",
                    ].map((sector, index) => (
                      <div
                        key={index}
                        className="bg-white p-3 rounded text-center shadow-sm"
                      >
                        <p className="font-medium text-gray-700">{sector}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Further Education
                  </h4>
                  <p className="text-gray-700 mb-4">
                    MSAI graduates can pursue advanced degrees such as:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    <li>PhD in Artificial Intelligence</li>
                    <li>PhD in Machine Learning</li>
                    <li>PhD in Data Science</li>
                    <li>PhD in Robotics</li>
                    <li>PhD in Computational Neuroscience</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-20 bg-gradient-to-r from-purple-800 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Become an AI Expert?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Join our MSAI program and develop the expertise needed to lead in
            the rapidly evolving field of artificial intelligence.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <ApplyButton
              to="/login"
              className="bg-white text-purple-700 hover:bg-purple-50 font-bold py-3 px-8 rounded-lg transition duration-300 shadow-lg transform hover:scale-105"
            >
              Apply Now
            </ApplyButton>
          </div>
        </div>
      </section> */}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default MsaiPage;
